import React, { useState, useEffect, useCallback, useRef, useMemo, useId } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import {
  getUsers, getUserStats, createUser, updateUser, deleteUser, banUser, unbanUser,
} from '../../api/users';
import ActionMenu from '../../components/ActionMenu/ActionMenu';
import Avatar, { avatarTone, initialsOf } from '../../components/Avatar/Avatar';
import { useConfirmDialog } from '../../components/ConfirmDialog/ConfirmDialog';
import Dropdown from '../../components/Dropdown/Dropdown';
import FilterSheet from '../../components/FilterSheet/FilterSheet';
import LoadingState from '../../components/LoadingState/LoadingState';
import MetricCard from '../../components/MetricCard/MetricCard';
import Modal from '../../components/Modal/Modal';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import PageHead from '../../components/PageHead/PageHead';
import Pagination from '../../components/Pagination/Pagination';
import SearchInput from '../../components/SearchInput/SearchInput';
import TableCard from '../../components/TableCard/TableCard';
import { useMediaQuery } from '../../components/Layout/shellState';
import './Team.css';
import {
  Pencil, CheckCircle, Ban, Trash2, LayoutList, LayoutGrid, Users, ShieldCheck, Presentation,
  GraduationCap, UserCog, BookOpen, Plus, X, UserRound,
} from 'lucide-react';
import { errorText } from '../../utils/errorText';
import { useI18n } from '../../i18n/I18nContext';
import { formatDate, formatPhone } from '../../utils/datetime';

/* ── Constants ────────────────────────────────────────────────────────────── */
const ROLES = ['', 'ADMIN', 'TEACHER', 'STUDENT', 'MODERATOR', 'MENTOR'];
// The five account roles the API knows, each with its own tile beside "everyone".
const COUNT_ROLES = ROLES.filter(Boolean);
const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 20;
const DEFAULT_SORT = 'createdAt:desc';
const VIEW_KEY = 'crm-team-view';

// One tone and icon per role: the filter tile, the badge and the person's row all agree, and red
// stays for "blocked" alone.
const ROLE_META = {
  '': { tone: 'brand', Icon: Users, titleKey: 'team.total' },
  ADMIN: { tone: 'coral', Icon: ShieldCheck, titleKey: 'team.admins' },
  TEACHER: { tone: 'purple', Icon: Presentation, titleKey: 'team.teachers' },
  STUDENT: { tone: 'success', Icon: GraduationCap, titleKey: 'team.students' },
  MODERATOR: { tone: 'teal', Icon: UserCog, titleKey: 'teams.people.moderators' },
  MENTOR: { tone: 'info', Icon: BookOpen, titleKey: 'teams.people.mentors' },
};
const EMPTY_FORM = { name: '', email: '', phone: '', role: 'STUDENT', password: '', isActive: true };
// i18n key of each role's name (the first four live in the base `team` dictionary).
const ROLE_KEY = {
  '': 'team.roleAll',
  ADMIN: 'team.roleAdmin',
  TEACHER: 'team.roleTeacher',
  STUDENT: 'team.roleStudent',
  MODERATOR: 'teams.people.roleModerator',
  MENTOR: 'teams.people.roleMentor',
};
// Account roles the member form can set.
const FORM_ROLES = ['STUDENT', 'TEACHER', 'ADMIN', 'MODERATOR', 'MENTOR'];

function readView() {
  try {
    return window.localStorage.getItem(VIEW_KEY) === 'table' ? 'table' : 'grid';
  } catch {
    return 'grid';
  }
}
function rememberView(view) {
  try { window.localStorage.setItem(VIEW_KEY, view); } catch { /* private window: the choice just does not stick */ }
}

function memberState(user) {
  if (user.isBanned) return { key: 'banned', tone: 'danger' };
  if (user.isActive) return { key: 'active', tone: 'success' };
  return { key: 'inactive', tone: 'neutral' };
}

function statusText(t, user) {
  if (user.isBanned) return t('common.banned');
  return user.isActive ? t('team.statusActive') : t('team.statusInactive');
}

/* ── Member form modal ────────────────────────────────────────────────────── */
function MemberModal({ member, onClose, onSave, onDelete }) {
  const { t } = useI18n();
  const uid = useId();
  const isEdit = !!member;
  const [form, setForm] = useState(() =>
    member
      ? { name: member.name || '', email: member.email || '', phone: member.phone || '', role: member.role || 'STUDENT', password: '', isActive: member.isActive ?? true }
      : { ...EMPTY_FORM }
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.name.trim()) return setErr(t('team.nameRequired'));
    if (!isEdit && !form.password) return setErr(t('team.passwordRequired'));
    setSaving(true); setErr(null);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined,
        role: form.role,
        ...(form.password ? { password: form.password } : {}),
      };
      let warning;
      if (isEdit) {
        await updateUser(member.id, { ...payload, isActive: form.isActive });
      } else {
        // POST /users has no isActive and rejects unknown fields: create the
        // account first, then switch it off when the form says Inactive.
        const created = await createUser(payload);
        if (!form.isActive && created?.id) {
          try { await updateUser(created.id, { isActive: false }); }
          catch { warning = t('teams.people.inactiveNotApplied'); }
        }
      }
      onSave(warning, { rolesChanged: !isEdit || payload.role !== member.role });
    } catch (e) {
      // An e-mail or phone that belongs to someone else is the one failure the person can fix at once
      setErr(e?.code === 409 ? t('teamCalendar.people.duplicate') : errorText(e, t('team.failedToSave')));
    } finally { setSaving(false); }
    return undefined;
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? t('team.editMember') : t('team.addTeamMember')}
      size="md"
      className="team-modal"
      as="form"
      onSubmit={handleSubmit}
      footer={(
        <>
          {isEdit && (
            <button type="button" className="ds-btn ds-btn-danger team-modal-delete" onClick={onDelete}>
              {t('common.delete')}
            </button>
          )}
          <button type="button" className="ds-btn ds-btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
          <button type="submit" className={`ds-btn ds-btn-primary${saving ? ' is-loading' : ''}`} disabled={saving}>
            {isEdit ? t('common.saveChanges') : t('team.addMember')}
          </button>
        </>
      )}
    >
      <div className="team-form">
        {err && <div className="ui-alert" role="alert"><div>{err}</div></div>}

        <div className="team-form-avatar">
          {/* The same tint the list gives this person; a new member starts on the brand tone, with a person
              until there is a name to take the initials from (not a "?") */}
          <span className="ui-avatar ui-avatar--lg" data-tone={isEdit ? avatarTone(member.id) : 'brand'} aria-hidden="true">
            {form.name.trim() ? initialsOf(form.name) : <UserRound />}
          </span>
        </div>

        <div className="ui-form-grid">
          <div className="ui-field is-wide">
            <label className="ui-label" htmlFor={`${uid}-name`}>{t('team.fullName')} <span className="ui-req" aria-hidden="true">*</span></label>
            <input id={`${uid}-name`} type="text" placeholder={t('team.namePlaceholder')} value={form.name} onChange={e => set('name', e.target.value)} autoComplete="off" data-autofocus />
          </div>

          <div className="ui-field">
            <label className="ui-label" htmlFor={`${uid}-email`}>{t('common.email')}</label>
            <input id={`${uid}-email`} type="email" placeholder={t('team.emailPlaceholder')} value={form.email} onChange={e => set('email', e.target.value)} autoComplete="off" />
          </div>

          <div className="ui-field">
            <label className="ui-label" htmlFor={`${uid}-phone`}>{t('common.phone')}</label>
            <input id={`${uid}-phone`} type="tel" placeholder={t('team.phonePlaceholder')} value={form.phone} onChange={e => set('phone', e.target.value)} autoComplete="off" />
          </div>

          <div className="ui-field">
            <span className="ui-label">{t('common.role')}</span>
            <Dropdown
              value={form.role}
              onChange={(val) => set('role', val)}
              options={FORM_ROLES.map((r) => ({ value: r, label: t(ROLE_KEY[r]) }))}
              ariaLabel={t('common.role')}
            />
          </div>

          <div className="ui-field">
            <span className="ui-label">{t('common.status')}</span>
            <Dropdown
              value={form.isActive ? 'true' : 'false'}
              onChange={(val) => set('isActive', val === 'true')}
              options={[
                { value: 'true', label: t('team.statusActive') },
                { value: 'false', label: t('team.statusInactive') },
              ]}
              ariaLabel={t('common.status')}
            />
          </div>

          <div className="ui-field is-wide">
            <label className="ui-label" htmlFor={`${uid}-password`}>{isEdit ? t('team.newPassword') : <>{t('team.password')} <span className="ui-req" aria-hidden="true">*</span></>}</label>
            <input id={`${uid}-password`} type="password" autoComplete="new-password" placeholder={isEdit ? t('team.passwordHint') : t('team.passwordPlaceholder')} value={form.password} onChange={e => set('password', e.target.value)} />
            {isEdit && <p className="ui-hint">{t('team.passwordHint')}</p>}
          </div>
        </div>
      </div>
    </Modal>
  );
}

/* ── Loading: placeholders the shape of the compact cards (the shared card skeleton is a tall course card) ── */
function MemberSkeleton({ count = 9 }) {
  const { t } = useI18n();
  return (
    <div className="card-grid team-grid" role="status" aria-busy="true" aria-live="polite">
      <span className="visually-hidden">{t('common.loading')}</span>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="team-card section-card team-skel" aria-hidden="true">
          <span className="ui-skel team-skel-avatar" />
          <div className="team-card-body">
            <span className="ui-skel team-skel-line" style={{ width: '58%' }} />
            <span className="ui-skel team-skel-line is-sub" style={{ width: '86%' }} />
          </div>
          <div className="team-card-foot">
            <span className="ui-skel team-skel-pill" />
            <span className="ui-skel team-skel-line is-sub team-skel-date" />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Member card (grid view) ──────────────────────────────────────────────── */
// A compact card: who, the role (and a status only when it is not the usual "active"), since when.
// The whole card opens the edit dialog through the name button (a stretched link); the "⋯" menu
// sits above it.
function MemberCard({ user, roleName, onEdit, onBan, onDelete }) {
  const { t } = useI18n();
  const state = memberState(user);
  const banLabel = user.isBanned ? t('team.unban') : t('team.ban');
  const contact = user.email || formatPhone(user.phone) || '—';
  return (
    <article className="team-card section-card is-interactive">
      <Avatar name={user.name} id={user.id} src={user.avatar} size="lg" decorative className="team-card-avatar" />
      <div className="team-card-body">
        <h2 className="team-card-name" title={user.name}>
          <button type="button" className="team-card-open" onClick={() => onEdit(user)}>{user.name || '—'}</button>
        </h2>
        <p className="team-card-email" title={contact}>{contact}</p>
      </div>
      <ActionMenu
        className="team-card-menu"
        ariaLabel={t('teamCalendar.people.actionsFor', { name: user.name || '' })}
        items={[
          { key: 'edit', label: t('common.edit'), icon: <Pencil />, onSelect: () => onEdit(user) },
          { key: 'ban', label: banLabel, icon: user.isBanned ? <CheckCircle /> : <Ban />, onSelect: () => onBan(user) },
          { key: 'delete', label: t('common.delete'), icon: <Trash2 />, tone: 'danger', onSelect: () => onDelete(user) },
        ]}
      />
      <div className="team-card-foot">
        <span className={`ds-badge ds-badge-${ROLE_META[user.role]?.tone || 'neutral'}`}>{roleName}</span>
        {state.key !== 'active' && (
          <span className={`ds-badge ds-badge--dot ds-badge-${state.tone}`}>{statusText(t, user)}</span>
        )}
        <span className="team-card-date" title={t('teamCalendar.people.joinedOn', { date: formatDate(user.createdAt) })}>
          {formatDate(user.createdAt)}
        </span>
      </div>
    </article>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   Main component

   `embedded` = drawn inside the Team hub, which owns the title. `actionsSlot` is the hub's cell
   next to its tabs: the primary button is portaled there; with no slot (a lone `<Team embedded />`)
   it is drawn in place.
   ════════════════════════════════════════════════════════════════════════════ */
function Team({ embedded = false, actionsSlot }) {
  const { t } = useI18n();
  const [users, setUsers]   = useState([]);
  const [meta, setMeta]     = useState(null);
  const [page, setPage]     = useState(1);
  const [search, setSearch] = useState('');
  const [role, setRole]     = useState('');
  const [sort, setSort]     = useState(DEFAULT_SORT);
  const [view, setView]     = useState(readView);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(false);
  const [roleCounts, setRoleCounts] = useState({});
  const [countsReady, setCountsReady] = useState(false);
  const isPhone = useMediaQuery('(max-width: 639px)', false);

  const [memberModal, setMemberModal] = useState(null); // null | { member? }
  const [ask, askDialog] = useConfirmDialog();

  // Only the newest list request may update the screen: typing or paging fast
  // must not let an older, slower response overwrite a newer one.
  const listReq = useRef(0);
  const countsReq = useRef(0);
  const searchTimer = useRef(null);

  const roleName = useCallback((r) => (ROLE_KEY[r] ? t(ROLE_KEY[r]) : (r || '—')), [t]);

  const sortOptions = useMemo(() => [
    { label: t('team.sortNewest'),  value: 'createdAt:desc' },
    { label: t('team.sortOldest'),  value: 'createdAt:asc'  },
    { label: t('team.sortNameAsc'), value: 'name:asc'        },
    { label: t('team.sortNameDesc'), value: 'name:desc'       },
  ], [t]);

  /* ── Fetch ──────────────────────────────────────────────────────────────── */
  const fetchUsers = useCallback(async (p, s, r, srt) => {
    const reqId = ++listReq.current;
    setLoading(true); setError(false);
    try {
      const [sortBy, sortOrder] = (srt || DEFAULT_SORT).split(':');
      const params = { page: p, limit: PAGE_SIZE, sortBy, sortOrder };
      if (s) params.search = s;
      if (r) params.role = r;
      const res = await getUsers(params);
      if (reqId !== listReq.current) return;
      const arr = Array.isArray(res) ? res : (res?.data || res?.items || []);
      setUsers(arr);
      setMeta(res?.meta || null);
    } catch (e) {
      if (reqId !== listReq.current) return;
      setError(true);
    } finally {
      if (reqId === listReq.current) setLoading(false);
    }
  }, []);

  // Head-counts are the API's totals (one GET /users/stats for all six tiles), never the rows on
  // screen. A failed count keeps the numbers already shown.
  const fetchCounts = useCallback(async () => {
    const reqId = ++countsReq.current;
    const stats = await getUserStats().catch(() => null);
    if (reqId !== countsReq.current) return;
    if (stats?.byRole) {
      setRoleCounts((prev) => {
        const next = { ...prev };
        COUNT_ROLES.forEach((r) => { if (typeof stats.byRole[r] === 'number') next[r] = stats.byRole[r]; });
        if (typeof stats.total === 'number') next.total = stats.total;
        return next;
      });
    }
    setCountsReady(true);
  }, []);

  useEffect(() => { fetchUsers(1, '', '', DEFAULT_SORT); fetchCounts(); }, [fetchUsers, fetchCounts]);
  useEffect(() => () => clearTimeout(searchTimer.current), []);

  // `counts: false` for a change that cannot move a head-count (a ban, a new name)
  const reload = ({ counts = true } = {}) => {
    clearTimeout(searchTimer.current);
    fetchUsers(page, search, role, sort);
    if (counts) fetchCounts();
  };

  /* ── Handlers ───────────────────────────────────────────────────────────── */
  // Typing only asks the API once the user pauses; the other controls fetch at once.
  const handleSearch = (s) => {
    setSearch(s); setPage(1);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => fetchUsers(1, s, role, sort), SEARCH_DEBOUNCE_MS);
  };
  const handleRole   = r => { clearTimeout(searchTimer.current); setRole(r); setPage(1); fetchUsers(1, search, r, sort); };
  const handleSort   = s => { clearTimeout(searchTimer.current); setSort(s); setPage(1); fetchUsers(1, search, role, s); };
  const handlePage   = p => { clearTimeout(searchTimer.current); setPage(p); fetchUsers(p, search, role, sort); };
  const clearFilters = () => { clearTimeout(searchTimer.current); setSearch(''); setRole(''); setPage(1); fetchUsers(1, '', '', sort); };
  // The "Filtrlar" sheet's own reset: the role and the sort go back to their defaults, the search stays
  const resetSheet  = () => { clearTimeout(searchTimer.current); setRole(''); setSort(DEFAULT_SORT); setPage(1); fetchUsers(1, search, '', DEFAULT_SORT); };
  const changeView   = (next) => { setView(next); rememberView(next); };

  // A role decides what a person can open: ask first, whichever way the change is made
  const handleRoleChange = async (user, newRole) => {
    if (!newRole || newRole === user.role) return;
    const ok = await ask({
      title: t('teamCalendar.people.roleChangeTitle'),
      description: [
        t('teamCalendar.people.roleChangeDesc', { name: user.name || '', from: roleName(user.role), to: roleName(newRole) }),
        newRole === 'ADMIN' ? t('teamCalendar.people.roleChangeAdmin') : '',
      ].filter(Boolean).join(' '),
      confirmLabel: t('teamCalendar.people.roleChangeConfirm'),
      tone: newRole === 'ADMIN' ? 'danger' : 'neutral',
    });
    if (!ok) return;
    try {
      await updateUser(user.id, { role: newRole });
      reload();
      toast.success(t('team.roleChanged', { name: user.name, role: roleName(newRole) }));
    } catch (e) { toast.error(errorText(e, t('team.failedToUpdateRole'))); }
  };

  const handleDelete = async (member) => {
    const ok = await ask({
      title: t('team.removeTitle'),
      description: t('team.removeMsg', { name: member.name }),
      confirmLabel: t('common.delete'),
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await deleteUser(member.id);
      // Removing the only row of the last page must not leave the list on a page that no longer exists.
      const nextPage = users.length === 1 && page > 1 ? page - 1 : page;
      clearTimeout(searchTimer.current);
      setPage(nextPage);
      fetchUsers(nextPage, search, role, sort);
      fetchCounts();
      toast.success(t('team.removedToast', { name: member.name }));
    } catch (e) { toast.error(errorText(e, t('team.failedToDelete'))); }
  };

  const handleBan = async (member) => {
    const unban = member.isBanned;
    const ok = await ask({
      title: t(unban ? 'team.unbanTitle' : 'team.banTitle'),
      description: t(unban ? 'team.unbanMsg' : 'team.banMsg', { name: member.name }),
      confirmLabel: t(unban ? 'team.unban' : 'team.ban'),
      tone: unban ? 'neutral' : 'danger',
    });
    if (!ok) return;
    try {
      if (unban) { await unbanUser(member.id); toast.success(t('team.unbannedToast', { name: member.name })); }
      else       { await banUser(member.id);   toast.success(t('team.bannedToast', { name: member.name })); }
      reload({ counts: false });
    } catch (e) { toast.error(errorText(e, t('team.failedToUpdateBan'))); }
  };

  // Until the API has answered there is no number to show: "0 members" would be a false claim
  const filtered = Boolean(search || role);
  const totalMembers = roleCounts.total ?? (!filtered ? meta?.total : null) ?? null;
  let countText = ' ';
  if (totalMembers != null) countText = totalMembers === 1 ? t('team.memberCountOne') : t('team.memberCount', { count: totalMembers });
  const activeView = isPhone ? 'grid' : view;

  /* ── Pieces ──────────────────────────────────────────────────────────────── */
  const addButton = (
    <button type="button" className="ds-btn ds-btn-primary" onClick={() => setMemberModal({})}>
      <Plus aria-hidden="true" /> {t('team.addMember')}
    </button>
  );
  // In the hub the button lives beside the tabs (portal); on its own it sits in the page header
  const placedAction = actionsSlot === undefined ? null : (actionsSlot ? createPortal(addButton, actionsSlot) : null);

  const roleFilter = (r) => ({
    key: r,
    label: t(ROLE_META[r].titleKey),
    count: roleCounts[r || 'total'],
    ...ROLE_META[r],
  });

  // From 640px the six roles are tiles above the toolbar. On a phone six boxes would be a screenful before
  // the first person: the roles fold, with the sort, into the "Filtrlar" sheet (chips with their counts).
  const roleTiles = isPhone ? null : (
    <div className="kpi-row team-kpis" role="group" aria-label={t('teamCalendar.people.rolesAria')}>
      {ROLES.map((r) => {
        const f = roleFilter(r);
        return (
          <MetricCard
            key={r || 'all'}
            compact
            title={f.label}
            value={f.count ?? '—'}
            icon={<f.Icon />}
            tone={f.tone}
            loading={!countsReady && f.count == null}
            active={role === r}
            onClick={() => handleRole(role === r ? '' : r)}
          />
        );
      })}
    </div>
  );

  const sortField = (
    <Dropdown
      value={sort}
      onChange={handleSort}
      options={sortOptions}
      className="team-sort-select"
      size="sm"
      ariaLabel={t('team.sort')}
    />
  );
  // What the sheet's button counts: a role other than "everyone", an order other than the default
  const filterCount = (role ? 1 : 0) + (sort !== DEFAULT_SORT ? 1 : 0);
  const sheetFilters = isPhone ? (
    <>
      <div className="ui-field">
        <span className="ui-label">{t('common.role')}</span>
        <div className="ui-chip-row ui-chip-row--wrap team-roles" role="group" aria-label={t('teamCalendar.people.rolesAria')}>
          {ROLES.map((r) => {
            const f = roleFilter(r);
            return (
              <button key={r || 'all'} type="button" className="ui-chip" aria-pressed={role === r} onClick={() => handleRole(role === r ? '' : r)}>
                {f.label}
                {f.count != null && <span className="ui-count">{f.count}</span>}
              </button>
            );
          })}
        </div>
      </div>
      <div className="ui-field">
        <span className="ui-label">{t('team.sort')}</span>
        {sortField}
      </div>
    </>
  ) : sortField;

  const pagination = meta && meta.totalPages > 1 ? (
    <Pagination
      page={page}
      totalPages={meta.totalPages}
      total={meta.total}
      limit={meta.limit || PAGE_SIZE}
      onPrev={() => handlePage(page - 1)}
      onNext={() => handlePage(page + 1)}
      hideOnSinglePage
    />
  ) : null;

  const editMember = (member) => setMemberModal({ member });

  let content;
  if (error) {
    content = <PageEmptyState variant="error" size="sm" onRetry={() => reload()} />;
  } else if (loading && users.length === 0) {
    content = activeView === 'grid' ? <MemberSkeleton /> : <LoadingState skeleton="table" rows={8} message={t('common.loading')} />;
  } else if (users.length === 0) {
    content = filtered ? (
      <PageEmptyState
        variant="search"
        title={t('team.emptyTitle')}
        description={t('team.emptyDesc')}
        actionLabel={t('teamCalendar.clearFilters')}
        onAction={clearFilters}
      />
    ) : (
      <PageEmptyState variant="students" title={t('teamCalendar.people.noneTitle')} description={t('teamCalendar.people.noneDesc')} />
    );
  } else if (activeView === 'grid') {
    content = (
      <>
        <div className={`card-grid team-grid${loading ? ' team-busy' : ''}`} aria-busy={loading || undefined}>
          {users.map(user => (
            <MemberCard
              key={user.id}
              user={user}
              roleName={roleName(user.role)}
              onEdit={editMember}
              onBan={handleBan}
              onDelete={handleDelete}
            />
          ))}
        </div>
        {pagination}
      </>
    );
  } else {
    content = (
      <div className={loading ? 'team-busy' : undefined} aria-busy={loading || undefined}>
        <TableCard className="team-table-card" aria-label={t('team.title')} footer={pagination}>
          <table className="ui-table ui-table--stack team-table">
            <thead>
              <tr>
                <th>{t('team.colMember')}</th>
                <th>{t('team.colContact')}</th>
                <th>{t('team.colRole')}</th>
                <th>{t('team.colStatus')}</th>
                <th className="team-joined">{t('team.colJoined')}</th>
                <th><span className="visually-hidden">{t('team.colActions')}</span></th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => {
                const state = memberState(user);
                const banLabel = user.isBanned ? t('team.unban') : t('team.ban');
                return (
                  <tr key={user.id}>
                    <td>
                      <div className="ui-cell-person">
                        <Avatar name={user.name} id={user.id} src={user.avatar} decorative />
                        <div className="ui-cell-person__text">
                          <span className="ui-cell-person__name">{user.name || '—'}</span>
                          <span className="ui-cell-person__sub">{user.email || '—'}</span>
                        </div>
                      </div>
                    </td>
                    <td data-label={t('team.colContact')} className="team-contact">{formatPhone(user.phone) || '—'}</td>
                    <td data-label={t('team.colRole')}>
                      <Dropdown
                        value={user.role}
                        onChange={(val) => handleRoleChange(user, val)}
                        options={FORM_ROLES.map((r) => ({ value: r, label: t(ROLE_KEY[r]) }))}
                        className="team-role-select"
                        size="sm"
                        ariaLabel={`${t('team.changeRole')}: ${user.name || ''}`}
                      />
                    </td>
                    <td data-label={t('team.colStatus')}>
                      <span className={`ds-badge ds-badge--dot ds-badge-${state.tone}`}>{statusText(t, user)}</span>
                    </td>
                    <td data-label={t('team.colJoined')} className="team-joined">{formatDate(user.createdAt)}</td>
                    <td className="ui-row-actions-cell">
                      <div className="ui-row-actions">
                        <button type="button" className="ui-icon-btn" title={t('common.edit')} aria-label={`${t('common.edit')}: ${user.name || ''}`} onClick={() => editMember(user)}><Pencil aria-hidden="true" /></button>
                        <button
                          type="button"
                          className={`ui-icon-btn${user.isBanned ? '' : ' ui-icon-btn--danger'}`}
                          title={banLabel}
                          aria-label={`${banLabel}: ${user.name || ''}`}
                          onClick={() => handleBan(user)}
                        >
                          {user.isBanned ? <CheckCircle aria-hidden="true" /> : <Ban aria-hidden="true" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableCard>
      </div>
    );
  }

  /* ── JSX ─────────────────────────────────────────────────────────────────── */
  return (
    <div className="team-page">
      {/* Standalone: its own header. In the hub the hub owns the title and the button is beside its tabs */}
      {!embedded && (
        <PageHead icon={<Users />} title={t('team.title')} description={countText} actions={addButton} />
      )}
      {embedded && actionsSlot === undefined && <div className="team-inline-actions">{addButton}</div>}
      {placedAction}

      {roleTiles}

      <div className="team-toolbar">
        <SearchInput
          className="team-search"
          placeholder={t('teamCalendar.people.searchPlaceholder')}
          aria-label={t('teamCalendar.people.searchPlaceholder')}
          value={search}
          onChange={handleSearch}
        />
        <FilterSheet count={filterCount} onClear={resetSheet}>
          {sheetFilters}
        </FilterSheet>
        {!isPhone && (
          <div className="team-view-toggle" role="group" aria-label={t('teamCalendar.viewMode')}>
            <button type="button" className={`team-view-btn${view === 'grid' ? ' active' : ''}`} title={t('team.viewGrid')} aria-label={t('team.viewGrid')} aria-pressed={view === 'grid'} onClick={() => changeView('grid')}><LayoutGrid aria-hidden="true" /></button>
            <button type="button" className={`team-view-btn${view === 'table' ? ' active' : ''}`} title={t('team.viewTable')} aria-label={t('team.viewTable')} aria-pressed={view === 'table'} onClick={() => changeView('table')}><LayoutList aria-hidden="true" /></button>
          </div>
        )}
      </div>

      {/* The role in force, told outside the sheet (the "Filtrlar" button only counts), with a way to drop it */}
      {isPhone && role && (
        <div className="team-applied">
          <span className="ui-chip ui-chip--removable">
            <span>{t(ROLE_META[role].titleKey)}</span>
            <button
              type="button"
              className="ui-chip__x"
              onClick={() => handleRole('')}
              aria-label={t('teamCalendar.removeFilter', { name: t(ROLE_META[role].titleKey) })}
            >
              <X aria-hidden="true" />
            </button>
          </span>
        </div>
      )}

      {content}

      {/* Member modal (add / edit) */}
      {memberModal && (
        <MemberModal
          member={memberModal.member || null}
          onClose={() => setMemberModal(null)}
          onDelete={() => { const member = memberModal.member; setMemberModal(null); handleDelete(member); }}
          onSave={(warning, { rolesChanged } = {}) => {
            const edited = Boolean(memberModal.member);
            setMemberModal(null);
            reload({ counts: rolesChanged !== false });
            if (warning) toast(warning, { duration: 6000 });
            else toast.success(edited ? t('team.memberUpdated') : t('team.memberAdded'));
          }}
        />
      )}

      {askDialog}
    </div>
  );
}

export default Team;
